// THE INBOX AT THE FRONT DOOR — the plate itself, shared by every front end that
// has one.
//
// PRESENTATION AND BEHAVIOUR, NO WIRING. It takes its rows, its counts and its verbs
// as props, and the client it should call as `request`. Each app keeps its own
// context and its own dress; what must not differ between them is what a lane means,
// which control a row is allowed to show, and what pressing it does. This repo
// already carries three drifted copies of Button and Toast — a drifted inbox would
// disagree about whether a security row can be dismissed.
//
// It lived inside the Settings window: behind a cog, two clicks from anywhere, and
// structurally unable to act — a row that navigated moved the page behind a window
// that stayed open, so clicking one appeared to do nothing at all.
//
// The seat sits in the bottom-left cluster with the other utilities rather than
// beside the avatar, where a tray seat was tried and pulled on 14 Sep for standing
// over the app's own figure. The cluster has the geometry for a fifth seat and
// cannot collide with anything.
//
// The Settings pane stays as the archive: everything, paginated, with preferences
// beside it. This is the live surface.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { actionsFor, hrefFor, canDismiss } from './index';

// Lane order is the order of demand: what needs you, what happened, what is on
// record. Never alphabetical, never by volume.
const LANES = [
    { id: 'queue', label: 'Needs you' },
    { id: 'feed', label: 'Happened' },
    { id: 'security', label: 'Security' },
];

const EMPTY = {
    queue: 'Nothing is waiting on you.',
    feed: 'Nothing has happened here yet. Shares, imports and automation runs arrive here.',
    security: 'No sign-ins, credential changes or grant changes recorded.',
};

/**
 * The lane the plate opens on: the first, in priority order, with something
 * unread. Needs you leads when it has anything; with nothing unread anywhere,
 * Happened, because it is the lane that always has a history behind it.
 *
 * Exported for the tests. Pure — it takes the counts, not the context.
 */
export function openingLane(laneCounts) {
    const unread = laneCounts || {};
    const first = LANES.find((l) => (unread[l.id] || 0) > 0);
    return first ? first.id : 'feed';
}

export function timeAgo(dateStr) {
    if (!dateStr) return '';
    const minutes = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
}

function DismissIcon() {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
        </svg>
    );
}

function InboxPlate({
    surface = 'nucleus', spaceLabel, onClose, onOpenArchive, onNavigate, request,
    notifications, unreadCount, loading, loadingMore, hasMore, loadMore,
    markRead, markAllRead, dismiss, lane, setLane, counts,
    permission, askPermission, soundEnabled, toggleSound,
}) {
    const plateRef = useRef(null);
    // Which row is mid-answer, so a double-press cannot send two of them.
    const [answering, setAnswering] = useState(null);

    // The tabs say one lane; the list must not show three.
    //
    // `lane` is null when nothing has picked one, which the API reads as "every
    // lane" — right for the settings-window archive, wrong here, where a tab is
    // always lit. So opening the plate names a lane, and closing it hands the
    // context back to the archive.
    //
    // WHICH LANE: the first, in priority order, with something unread in it.
    // Needs you leads when it has anything — it is the lane that is waiting on
    // you — but opening on it when it is empty, over a Happened tab holding a
    // hundred unread rows, reads as a broken panel. Reported live as "it shows
    // nothing, it resolved everything". With nothing unread anywhere, Happened:
    // it is the lane that always has a history to show.
    useEffect(() => {
        if (!lane) setLane(openingLane(counts?.lanes));
        return () => setLane(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close on an outside press or Escape, the way every other float in the fleet
    // behaves. data-seat on the opener is what keeps its own re-press working.
    useEffect(() => {
        const onDown = (e) => {
            if (plateRef.current && !plateRef.current.contains(e.target)
                && !e.target.closest?.('[data-seat="inbox"]')) onClose();
        };
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    const activeLane = lane || 'queue';
    // FILTERED HERE AS WELL AS ON THE SERVER. The context's rows are whatever its
    // last fetch returned, and on first open that fetch was lane=null — every
    // lane, for the badge. Rendering them unfiltered while the lane fetch is in
    // flight showed all three lanes for a beat and then emptied, which is the
    // flash reported as "it shows happened and auth, then nothing". A row carries
    // its lane, so the filter is instant and the list is right at every frame.
    const rows = (notifications || []).filter((n) => n.lane === activeLane);
    const laneCounts = counts?.lanes || {};

    // Answer a queue row where it stands. It clears by being answered, which is the
    // whole difference between this lane and the other two.
    const answer = async (n, action) => {
        if (answering) return;
        if (action.navigate) { onClose(); onNavigate({ url: action.navigate }); return; }

        setAnswering(n.id);
        try {
            const result = await request(action.request.path, {
                method: action.request.method,
                body: action.request.body,
            });
            markRead(n.id);
            // Joining a group means choosing what you share with it, which is a
            // consent screen and not a button — the API says where to go.
            if (action.followRedirect && result?.redirect_url) {
                onClose();
                onNavigate({ url: result.redirect_url });
            }
        } catch {
            // Leave the row standing: an answer that did not land must not look
            // like one that did.
        } finally {
            setAnswering(null);
        }
    };

    const open = (n) => {
        if (!n.is_read) markRead(n.id);
        const to = hrefFor(n, surface, counts?.space || 'personal');
        if (!to) return;
        onClose();
        onNavigate(to);
    };

    return createPortal(
        <div className="inbox-plate" ref={plateRef} role="dialog" aria-label="Inbox">
            <div className="inbox-plate__head">
                <span className="inbox-plate__title">
                    Inbox{unreadCount > 0 ? ` · ${unreadCount}` : ''}
                </span>
                <span className="inbox-plate__space">{spaceLabel}</span>
                {unreadCount > 0 && (
                    <button type="button" className="inbox-plate__verb" onClick={markAllRead}>
                        Mark all read
                    </button>
                )}
            </div>

            <div className="inbox-plate__lanes" role="tablist">
                {LANES.map((l) => (
                    <button
                        key={l.id}
                        type="button"
                        role="tab"
                        aria-selected={activeLane === l.id}
                        className={`inbox-plate__lane${activeLane === l.id ? ' inbox-plate__lane--on' : ''}`}
                        onClick={() => setLane(l.id)}
                    >
                        {l.label}
                        {laneCounts[l.id] > 0 && (
                            <span className="inbox-plate__lane-count">{laneCounts[l.id]}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* THE PERMISSION ASK, AT A MOMENT THAT EXPLAINS ITSELF.
                Never on page load: a prompt with no context is refused once and
                then unavailable forever. It is offered here, under the lane whose
                rows are the reason to want it, and only while there is something
                waiting. */}
            {permission === 'default' && activeLane === 'queue' && rows.length > 0 && (
                <div className="inbox-plate__ask">
                    <span>Get told when something needs you, even in another tab.</span>
                    <button type="button" className="inbox-plate__verb" onClick={askPermission}>
                        Turn on
                    </button>
                </div>
            )}

            <div className="inbox-plate__list">
                {loading && rows.length === 0 ? (
                    <p className="inbox-plate__empty">Loading…</p>
                ) : rows.length === 0 ? (
                    <p className="inbox-plate__empty">{EMPTY[activeLane]}</p>
                ) : (
                    rows.map((n) => (
                        <div
                            key={n.id}
                            className={`inbox-plate__row${n.is_read ? '' : ' inbox-plate__row--unread'}`}
                        >
                            {/* A row with nowhere to go is still worth clicking —
                                it marks itself read — but it must not promise a
                                destination it does not have. Thousands of rows
                                predate the referent columns. */}
                            <button
                                type="button"
                                className={`inbox-plate__row-open${hrefFor(n, surface, counts?.space || 'personal') ? '' : ' inbox-plate__row-open--inert'}`}
                                onClick={() => open(n)}
                            >
                                <span className="inbox-plate__dot" aria-hidden="true" />
                                <span className="inbox-plate__body">
                                    <span className="inbox-plate__row-title">{n.title}</span>
                                    {n.message && (
                                        <span className="inbox-plate__row-message">{n.message}</span>
                                    )}
                                </span>
                                <span className="inbox-plate__time">{timeAgo(n.created_at)}</span>
                            </button>
                            {/* A queue row's answer lives on the row. */}
                            {actionsFor(n).length > 0 && (
                                <span className="inbox-plate__actions">
                                    {actionsFor(n).map((action) => (
                                        <button
                                            key={action.id}
                                            type="button"
                                            disabled={answering === n.id}
                                            className={`inbox-plate__act${action.primary ? ' inbox-plate__act--primary' : ''}`}
                                            onClick={() => answer(n, action)}
                                        >
                                            {action.label}
                                        </button>
                                    ))}
                                </span>
                            )}
                            {/* Only what the API lets you dismiss (canDismiss): a feed row
                                addressed to you. Offering it elsewhere would be a lie. */}
                            {canDismiss(n) && (
                                <button
                                    type="button"
                                    className="inbox-plate__dismiss"
                                    aria-label={`Dismiss: ${n.title}`}
                                    onClick={() => dismiss(n.id)}
                                >
                                    <DismissIcon />
                                </button>
                            )}
                        </div>
                    ))
                )}

                {hasMore && rows.length > 0 && (
                    <button type="button" className="inbox-plate__more" onClick={loadMore} disabled={loadingMore}>
                        {loadingMore ? 'Loading…' : 'Older'}
                    </button>
                )}
            </div>

            <div className="inbox-plate__foot">
                <button
                    type="button"
                    className="inbox-plate__sound"
                    aria-pressed={soundEnabled}
                    aria-label={soundEnabled ? 'Sound on' : 'Sound off'}
                    onClick={toggleSound}
                >
                    {soundEnabled ? 'Sound on' : 'Sound off'}
                </button>
                <button type="button" className="inbox-plate__verb" onClick={onOpenArchive}>
                    Everything
                </button>
            </div>
        </div>,
        document.body,
    );
}

export default InboxPlate;
